import { z } from "zod";
import { DocumentTextSchema } from "./prose.js";
import { FactIdSchema, FidelitySchema } from "./primitives.js";
/**
 * Documents (ADR 0003, ADR 0007; Phase 5 task section 1). A Document's
 * `body` is a discriminated union on `kind` — every DocumentKind gets a
 * real structured shape. There is no `content: string` escape hatch on any
 * kind: that would let a fact slip into a document unstructured, defeating
 * the referential-integrity guard ADR 0003 is built on.
 *
 * Free-text fields inside a body (paragraphs, captions, footnotes, margin
 * notes, postit text, transcript lines, slide bullets) are DocumentText,
 * not Prose — see docs/RECONCILIATION.md's "Contracts defect fix" note:
 * Prose's banned list corrupts ordinary business vocabulary that belongs in
 * real case content. Short labels/names/titles stay plain strings, the same
 * distinction CaseBibleSchema already draws between Prose fields and label
 * fields.
 *
 * Every kind carries doc_id, title, authored_by, created_in_fiction_at, and
 * declared_fact_ids[] (task section 1's explicit shared shape) — `doc_id`
 * replaces the prior `id` field name, aligning with CaseBibleDocumentSchema
 * and packages/db's `case_documents.doc_id`, which already used this name.
 */
// ---------------------------------------------------------------------------
// FACT ANCHORS — where in the body a declared fact actually lives
// ---------------------------------------------------------------------------
/**
 * An RFC 6901 JSON pointer into a document's `body` ("/rows/3/values/1",
 * "/sections/0/paragraphs/2", "/chart_spec/series/0/values/4"). Starts with
 * "/" and names at least one token; "~1" and "~0" escape "/" and "~" inside
 * a token, exactly as the RFC says. A pointer is a path, never a value:
 * nothing about the fact's content is written here (Law 3: the fact still
 * only reaches the learner through the rendered document).
 */
export const BodyPointerSchema = z
    .string()
    .min(2, "a body pointer names at least one token")
    .regex(/^(?:\/(?:[^/~]|~0|~1)*)+$/, "a body pointer is an RFC 6901 JSON pointer starting with '/'");
/**
 * One declared fact's evidence in this document: EVERY body fragment a
 * reader needs in order to see the fact. Visibility after a fidelity
 * transform is then a question of fact, not of judgement — the fact is
 * visible only when every anchored value survives the transform unchanged
 * (packages/documents' visibleFactIds). Two fragments that each carry the
 * fact on their own are two facts' worth of evidence and should be two
 * anchors on two facts, or one anchor on the one fragment the author is
 * willing to stand behind; this list is a conjunction, never a choice.
 */
export const FactAnchorSchema = z.strictObject({
    fact_id: FactIdSchema,
    /** All required fragments. Unique, non-empty, pointers into `body`. */
    requires: z
        .array(BodyPointerSchema)
        .min(1, "a fact anchor requires at least one body pointer")
        .refine((pointers) => new Set(pointers).size === pointers.length, { message: "a fact anchor's pointers must be unique" }),
});
const documentBaseShape = {
    doc_id: z.string().min(1),
    title: z.string().min(1),
    /** npc id, the literal "self" (ADR 0003), or "client_system". */
    authored_by: z.string().min(1),
    /** An in-fiction date/timestamp string — never a mechanical turn number. */
    created_in_fiction_at: z.string().min(1),
    /** The whitelist this document is allowed to expose — ADR 0003's referential-integrity anchor. */
    declared_fact_ids: z.array(FactIdSchema),
    /**
     * Optional, additive (N09, step 4a): per declared fact, the body
     * fragments that carry it. Absent on every document authored before it
     * existed; those facts are "unanchored" and admitted after a transform
     * only when the whole original body demonstrably survives. Anchors may
     * name only declared facts, at most once each (refined on the document
     * schemas below, where both fields are in view). On an ORIGINAL document
     * every pointer must also resolve into the body; on a DELIVERED one it
     * need not, because a transform may have removed exactly the fragment the
     * anchor names — which is the point.
     */
    fact_anchors: z.array(FactAnchorSchema).optional(),
};
// ---------------------------------------------------------------------------
// EMAIL
// ---------------------------------------------------------------------------
const EmailAttachmentSchema = z.strictObject({
    name: z.string().min(1),
    /** The kind of document this attachment would be, once delivered. */
    kind: z.string().min(1),
    /**
     * A named attachment that is NOT yet delivered (doc_id: null) is a
     * channel the learner can request (task section 1) — its existence is
     * visible before its content is.
     */
    doc_id: z.string().min(1).nullable(),
});
const EmailBodySchema = z.strictObject({
    from: z.string().min(1),
    to: z.array(z.string().min(1)).min(1),
    cc: z.array(z.string().min(1)),
    sent: z.string().min(1),
    subject: z.string().min(1),
    attachments: z.array(EmailAttachmentSchema),
    paragraphs: z.array(DocumentTextSchema).min(1),
    signature: DocumentTextSchema,
    thread_parent: z.string().min(1).nullable(),
});
// ---------------------------------------------------------------------------
// FINANCIAL_TABLE
// ---------------------------------------------------------------------------
/**
 * The grain a row/column is expressed at — what the AGGREGATED fidelity
 * collapses along. Three levels, named neutrally so every case can use them:
 * LEAF is the finest authored grain (a clinic, a branch, a customer account,
 * a SKU), MID is the roll-up the client reports at (a region, a customer
 * segment, a product category), TOTAL is the company. A case names its own
 * levels in column labels and footnotes; the enum only fixes the depth.
 * Renamed from CLINIC/REGION/TOTAL (Harborlight's words) — see
 * docs/case-authoring/01-vocabularies.md §14.
 */
export const AggregationLevelSchema = z.enum(["LEAF", "MID", "TOTAL"]);
const FinancialTableColumnSchema = z.strictObject({
    label: z.string().min(1),
    dimension: z.string().min(1),
    aggregation_level: AggregationLevelSchema,
});
const FinancialTableRowSchema = z.strictObject({
    label: z.string().min(1),
    level: AggregationLevelSchema,
    /**
     * Which next-level-up row this rolls into when the AGGREGATED fidelity
     * collapses levels — e.g. a LEAF row's `rollup_parent` names its
     * MID row's `label`. Null for TOTAL rows (nothing to roll into) or a
     * genuinely standalone row. Not in the task's literal field list; added
     * because computing a real, arithmetically-correct roll-up (task section
     * 2's explicit hard requirement) needs SOME way to know which rows
     * group together, and "label, level, values" alone cannot express that.
     */
    rollup_parent: z.string().nullable(),
    values: z.array(z.number()),
});
/**
 * task section 1 lists margin_notes' shape as "(author, anchor: row/column/
 * cell, text)" — read here as "anchor is a reference to a row, a column, or
 * a cell" (a discriminated union carrying the actual row/column label(s) it
 * points at), not a bare 3-value enum with no target — an enum alone
 * couldn't anchor a note to anything. This is an interpretive addition
 * beyond the literal field list, not a literal transcription; flagged here
 * rather than in RECONCILIATION.md since it is a small, load-bearing
 * modeling choice made necessary by the spec text itself, not a genuine
 * ambiguity between two documents.
 */
const MarginNoteAnchorSchema = z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("row"), row_label: z.string().min(1) }),
    z.strictObject({ type: z.literal("column"), column_label: z.string().min(1) }),
    z.strictObject({ type: z.literal("cell"), row_label: z.string().min(1), column_label: z.string().min(1) }),
]);
const MarginNoteSchema = z.strictObject({
    author: z.string().min(1),
    anchor: MarginNoteAnchorSchema,
    text: DocumentTextSchema,
});
const FinancialTableBodySchema = z.strictObject({
    title: z.string().min(1),
    period: z.string().min(1),
    units: z.string().min(1),
    currency: z.string().min(1),
    columns: z.array(FinancialTableColumnSchema).min(1),
    rows: z.array(FinancialTableRowSchema).min(1),
    footnotes: z.array(DocumentTextSchema),
    /** Handwritten annotations on a printed pack — design_frames.pdf frame 11. */
    margin_notes: z.array(MarginNoteSchema),
    source: z.string().min(1),
    as_of_date: z.string().min(1),
    revision: z.string().min(1).nullable(),
});
// ---------------------------------------------------------------------------
// MEMO
// ---------------------------------------------------------------------------
const MemoSectionSchema = z.strictObject({
    heading: z.string().min(1),
    paragraphs: z.array(DocumentTextSchema).min(1),
});
const MemoBodySchema = z.strictObject({
    author: z.string().min(1),
    date: z.string().min(1),
    distribution: z.array(z.string().min(1)),
    sections: z.array(MemoSectionSchema).min(1),
});
// ---------------------------------------------------------------------------
// POSTIT
// ---------------------------------------------------------------------------
const POSTIT_MAX_WORDS = 40;
function countWords(text) {
    return text.trim().split(/\s+/).filter(Boolean).length;
}
/**
 * A jotted list of who is who. Names and ROLES ONLY — no disposition, no
 * agenda, no hint about who is worth asking what. The gatekeeper structure is
 * one of the best things in the case, and it was invisible: knowing to ask
 * Marcus for operational data rather than Renata required having read the case
 * bible. A learner has no such list, and inferring an org chart from cold opens
 * is not the skill under test.
 *
 * Roles are one short phrase. Anything longer stops being a list and starts
 * being a briefing, which is exactly what this must not become.
 */
const CAST_ROLE_MAX_WORDS = 8;
const PostitCastEntrySchema = z.strictObject({
    name: z.string().min(1),
    role: DocumentTextSchema.refine((value) => countWords(value) <= CAST_ROLE_MAX_WORDS, {
        message: `a cast role must be a short phrase — at most ${CAST_ROLE_MAX_WORDS} words`,
    }),
});
const POSTIT_ASIDE_MAX_WORDS = 25;
const PostitBodySchema = z.strictObject({
    /** The literal "self" (the learner's own handwriting, ADR 0003) or an npc id. */
    author: z.string().min(1),
    text: DocumentTextSchema.refine((value) => countWords(value) <= POSTIT_MAX_WORDS, {
        message: `a post-it's text must be short — at most ${POSTIT_MAX_WORDS} words`,
    }),
    color: z.string().min(1),
    /** Where/what it's physically stuck to, diegetically — e.g. "top of the financial report". */
    anchor_hint: z.string().min(1),
    /**
     * Optional, and capped low on purpose: a post-it that lists nine people is a
     * dossier. Empty for every ordinary post-it in a case; used by the one the
     * learner writes to themselves before day one.
     */
    cast: z.array(PostitCastEntrySchema).max(6).default([]),
    /**
     * One more line in the same hand, written at a different moment. Held apart
     * from `text` rather than appended to it so the renderer can set it as the
     * afterthought it is, and so `text` stays under its own word cap.
     *
     * DocumentText, so the diegetic-language rule applies to it exactly as it does
     * to every other learner-facing string: it may imply that something can go
     * badly and be approached another way, and it may not say so mechanically.
     */
    aside: DocumentTextSchema.refine((value) => countWords(value) <= POSTIT_ASIDE_MAX_WORDS, {
        message: `a post-it's aside must be short — at most ${POSTIT_ASIDE_MAX_WORDS} words`,
    }).nullable().default(null),
});
// ---------------------------------------------------------------------------
// DATAROOM_INDEX
// ---------------------------------------------------------------------------
const DataroomEntrySchema = z.strictObject({
    label: z.string().min(1),
    channel: z.string().min(1),
    gated: z.boolean(),
    /** Expressed diegetically ("requires CFO sign-off"), never mechanically — task section 3. */
    gate_reason: DocumentTextSchema.nullable(),
    /**
     * WHO the gate is — the NPC (by bible name) a declined request is voiced
     * through, and the person a learner would go ask. Authored per entry because
     * the reasons already point at owners ("Held by Operations", "requires
     * sponsor sign-off") without naming one, and a decline with nobody in it
     * gave the narrator a scene with no people — the thin-brief turns that
     * reliably shipped the authored fallback. Null on ungated entries and on
     * gates that are genuinely institutional rather than personal.
     */
    gatekeeper: z.string().min(1).nullable().default(null),
    /**
     * THE HOLDER'S OWN WORDS (ADR 0020, stage 3). The note the gatekeeper sends when the file
     * comes round to the learner by another door. Codex writes it; absent, the engine composes
     * one from `gate_reason`. Never rendered with the index: the render plan picks its fields.
     */
    reach_out_line: DocumentTextSchema.optional(),
    /**
     * A file its holder thinks twice about (stage 3). The one effect: even the right person
     * answers a turn later, except on the best band, and the note carries `reach_out_line`.
     * At most three per case, and never the only road to a CORE fact (case:check V58).
     */
    sensitivity: z.literal("SENSITIVE").optional(),
    /**
     * A label that could mean more than one file (stage 3). The person asked puts `question`;
     * the learner's reply is matched against each option's `phrases` by code, and anything
     * else takes `default_doc_id`. No model reads or chooses (case:check V59).
     */
    clarifier: z
        .strictObject({
        question: DocumentTextSchema,
        /** The bare ways a learner says it ("the schedule"). An ask that says one of these and none of an option's phrases is the ambiguous ask this slot answers. */
        said_as: z.array(z.string().min(1)).min(1).optional(),
        options: z.array(z.strictObject({ phrases: z.array(z.string().min(1)).min(1), doc_id: z.string().min(1) })).min(2).max(4),
        default_doc_id: z.string().min(1),
    })
        .optional(),
    /** Null until the entry has actually been delivered as a real document. */
    doc_id: z.string().min(1).nullable(),
    last_updated: z.string().min(1),
});
const DataroomFolderSchema = z.strictObject({
    name: z.string().min(1),
    entries: z.array(DataroomEntrySchema),
});
const DataroomIndexBodySchema = z.strictObject({
    folders: z.array(DataroomFolderSchema).min(1),
});
// ---------------------------------------------------------------------------
// EXHIBIT
// ---------------------------------------------------------------------------
const ChartSeriesSchema = z.strictObject({
    name: z.string().min(1),
    values: z.array(z.number()),
});
const ChartAxisSchema = z.strictObject({
    label: z.string().min(1),
    unit: z.string().min(1),
    categories: z.array(z.string().min(1)),
});
/** A data-only spec: series, axes, units — NEVER a rendered image (task section 1). */
const ChartSpecSchema = z.strictObject({
    chart_type: z.enum(["line", "bar", "scatter", "pie", "table"]),
    series: z.array(ChartSeriesSchema).min(1),
    x_axis: ChartAxisSchema,
    y_axis: ChartAxisSchema,
});
const ExhibitBodySchema = z.strictObject({
    title: z.string().min(1),
    chart_spec: ChartSpecSchema,
    caption: DocumentTextSchema,
    source: z.string().min(1),
});
// ---------------------------------------------------------------------------
// TRANSCRIPT
// ---------------------------------------------------------------------------
const TranscriptTurnSchema = z.strictObject({
    speaker: z.string().min(1),
    text: DocumentTextSchema,
    non_verbal: z.string().min(1).nullable(),
});
const TranscriptRedactionSchema = z.strictObject({
    turn_index: z.number().int().min(0),
    reason: z.string().min(1),
});
const TranscriptBodySchema = z.strictObject({
    participants: z.array(z.string().min(1)).min(1),
    date: z.string().min(1),
    setting: z.string().min(1),
    turns: z.array(TranscriptTurnSchema).min(1),
    redactions: z.array(TranscriptRedactionSchema).nullable(),
    /**
     * Handwritten annotations on a printed transcript — same treatment as a
     * financial table's margin notes (structure-and-rewards phase: the
     * unnamed hand is now formally a MARGIN_NOTE promise channel, and it
     * writes on more than financial tables). Anchored by turn index.
     */
    margin_notes: z.array(z.strictObject({
        author: z.string().min(1),
        turn_index: z.number().int().min(0),
        text: DocumentTextSchema,
    })),
});
// ---------------------------------------------------------------------------
// ENVIRONMENT
// ---------------------------------------------------------------------------
/**
 * A lived scene (structure-and-rewards phase, section 6): the learner goes
 * somewhere and PERCEIVES the situation — no data room, no interview. It is
 * still a Document because facts may only reach the learner inside a
 * whitelisted document (Law 3): the scene is captured diegetically as the
 * learner's own field impressions, written down afterwards, and its
 * declared_fact_ids work exactly like any other document's.
 */
const EnvironmentMomentSchema = z.strictObject({
    /** What the learner sees/hears, present tense, sensory. */
    observation: DocumentTextSchema,
    /** Optional overheard speech, verbatim. */
    overheard: DocumentTextSchema.nullable(),
});
const EnvironmentBodySchema = z.strictObject({
    /** Where the learner physically is. */
    location: z.string().min(1),
    /** Time of day / day of week, in-fiction. */
    when: z.string().min(1),
    /** The scene, moment by moment. */
    moments: z.array(EnvironmentMomentSchema).min(2),
    /** The learner's own one-line takeaway, in their voice. */
    field_note: DocumentTextSchema,
});
// ---------------------------------------------------------------------------
// SLIDE
// ---------------------------------------------------------------------------
const SlideBodySchema = z.strictObject({
    title: z.string().min(1),
    kicker: z.string().min(1),
    bullets: z.array(DocumentTextSchema).min(1),
    exhibit_ref: z.string().min(1).nullable(),
    footer: z.string().min(1),
    deck_name: z.string().min(1),
    slide_number: z.number().int().min(1),
});
// ---------------------------------------------------------------------------
// The discriminated union
// ---------------------------------------------------------------------------
const EmailDocumentSchema = z.strictObject({ ...documentBaseShape, kind: z.literal("EMAIL"), body: EmailBodySchema });
const FinancialTableDocumentSchema = z.strictObject({
    ...documentBaseShape,
    kind: z.literal("FINANCIAL_TABLE"),
    body: FinancialTableBodySchema,
});
const MemoDocumentSchema = z.strictObject({ ...documentBaseShape, kind: z.literal("MEMO"), body: MemoBodySchema });
const PostitDocumentSchema = z.strictObject({ ...documentBaseShape, kind: z.literal("POSTIT"), body: PostitBodySchema });
const DataroomIndexDocumentSchema = z.strictObject({
    ...documentBaseShape,
    kind: z.literal("DATAROOM_INDEX"),
    body: DataroomIndexBodySchema,
});
const ExhibitDocumentSchema = z.strictObject({ ...documentBaseShape, kind: z.literal("EXHIBIT"), body: ExhibitBodySchema });
const TranscriptDocumentSchema = z.strictObject({
    ...documentBaseShape,
    kind: z.literal("TRANSCRIPT"),
    body: TranscriptBodySchema,
});
const SlideDocumentSchema = z.strictObject({ ...documentBaseShape, kind: z.literal("SLIDE"), body: SlideBodySchema });
const EnvironmentDocumentSchema = z.strictObject({ ...documentBaseShape, kind: z.literal("ENVIRONMENT"), body: EnvironmentBodySchema });
const documentVariants = [
    EmailDocumentSchema,
    FinancialTableDocumentSchema,
    MemoDocumentSchema,
    PostitDocumentSchema,
    DataroomIndexDocumentSchema,
    ExhibitDocumentSchema,
    TranscriptDocumentSchema,
    SlideDocumentSchema,
    EnvironmentDocumentSchema,
];
// ---------------------------------------------------------------------------
// Fact anchor integrity: the checks that need `declared_fact_ids`, `body`
// and `fact_anchors` together, so they live on the document, not the field.
// ---------------------------------------------------------------------------
const ARRAY_INDEX = /^(?:0|[1-9]\d*)$/;
/**
 * RFC 6901 resolution of `pointer` against `body`. Pure; never throws.
 * Array tokens must be canonical non-negative integers (no "-", no leading
 * zeros); object tokens must be own properties. Anything else, or any step
 * through a scalar, is `found: false` — a pointer that does not resolve is a
 * fact whose evidence is not there, and that is the answer, not an error.
 */
export function resolveBodyPointer(body, pointer) {
    if (!BodyPointerSchema.safeParse(pointer).success)
        return { found: false };
    const tokens = pointer
        .slice(1)
        .split("/")
        .map((token) => token.replaceAll("~1", "/").replaceAll("~0", "~"));
    let current = body;
    for (const token of tokens) {
        if (Array.isArray(current)) {
            if (!ARRAY_INDEX.test(token))
                return { found: false };
            const index = Number(token);
            if (index >= current.length)
                return { found: false };
            current = current[index];
            continue;
        }
        if (typeof current === "object" && current !== null && Object.prototype.hasOwnProperty.call(current, token)) {
            current = current[token];
            continue;
        }
        return { found: false };
    }
    return { found: true, value: current };
}
/**
 * Every way a document's fact anchors can be wrong, as messages. Structural
 * problems (undeclared fact, duplicate fact, duplicate pointer) are always
 * reported; unresolvable pointers only when `requireResolution` — true for
 * an original document (the author pointed at nothing), false for a
 * delivered one (the transform may have removed it on purpose). Empty means
 * sound. Exported so authoring tools can report the same findings the
 * schema rejects.
 */
export function factAnchorIssues(doc, options = { requireResolution: true }) {
    const anchors = doc.fact_anchors ?? [];
    const issues = [];
    const declared = new Set(doc.declared_fact_ids);
    const seen = new Set();
    anchors.forEach((anchor, index) => {
        if (!declared.has(anchor.fact_id))
            issues.push(`fact_anchors[${String(index)}]: ${anchor.fact_id} is not in declared_fact_ids`);
        if (seen.has(anchor.fact_id))
            issues.push(`fact_anchors[${String(index)}]: ${anchor.fact_id} is anchored more than once`);
        seen.add(anchor.fact_id);
        if (new Set(anchor.requires).size !== anchor.requires.length)
            issues.push(`fact_anchors[${String(index)}]: duplicate pointer for ${anchor.fact_id}`);
        if (options.requireResolution) {
            for (const pointer of anchor.requires) {
                if (!resolveBodyPointer(doc.body, pointer).found)
                    issues.push(`fact_anchors[${String(index)}]: ${pointer} does not resolve in the body of ${anchor.fact_id}`);
            }
        }
    });
    return issues;
}
function refineFactAnchors(requireResolution) {
    return (doc, ctx) => {
        for (const issue of factAnchorIssues(doc, { requireResolution })) {
            ctx.addIssue({ code: "custom", message: issue, path: ["fact_anchors"] });
        }
    };
}
export const DocumentSchema = z.discriminatedUnion("kind", documentVariants).superRefine(refineFactAnchors(true));
/**
 * Fidelity is applied to a Document by packages/documents' fidelity
 * transformer; it is never authored. DeliveredDocument is deliberately
 * JUST `Document & { fidelity }` — no `delivered_at_turn` field: that is
 * session-scoped orchestration state (packages/db's
 * `session_documents.delivered_at_turn` relational column already owns
 * it), not part of the document's own content shape, and
 * `transform(doc, fidelity)` (task section 2) has no turn number to stamp
 * one with in the first place — it is a pure, two-argument function.
 */
const deliveredFields = { fidelity: FidelitySchema };
export const DeliveredDocumentSchema = z
    .discriminatedUnion("kind", [
    EmailDocumentSchema.extend(deliveredFields),
    FinancialTableDocumentSchema.extend(deliveredFields),
    MemoDocumentSchema.extend(deliveredFields),
    PostitDocumentSchema.extend(deliveredFields),
    DataroomIndexDocumentSchema.extend(deliveredFields),
    ExhibitDocumentSchema.extend(deliveredFields),
    TranscriptDocumentSchema.extend(deliveredFields),
    SlideDocumentSchema.extend(deliveredFields),
    EnvironmentDocumentSchema.extend(deliveredFields),
])
    // Structural integrity only: a delivered document's anchors may point at
    // fragments the transform removed. That is what visibleFactIds reads.
    .superRefine(refineFactAnchors(false));
//# sourceMappingURL=document.js.map